import { useEffect, useRef, RefObject, useMemo } from 'react';
import { AUTHOR_NEXA_AGENT, AUTHOR_USER } from '../utils';
import { NexaEvent } from '../NexaStore';

// FunctionCall from raw events - contains only id and name, not the full args
export interface FunctionCall {
  id: string;
  value: {
    id: string; // adk-... id used to look up config in dashboardConfigMap
    name: string; // tool name like 'metrics_query'
  };
}

export interface ProcessedNexaEvent extends NexaEvent {
  isLastAgentInSequence?: boolean;
  isLastAgentEventOverall?: boolean;
  hasNonThoughtAfter?: boolean;
  precedingUserQuery?: string;
  functionCalls?: FunctionCall[];
}

/**
 * Custom hook to process events and determine which ones are the last agent messages in a sequence
 *
 * @param events - Array of chat events
 * @param isStreaming - Boolean indicating if a message is currently streaming
 */
export const useProcessedEvents = (
  events: NexaEvent[],
  isStreaming: boolean,
): ProcessedNexaEvent[] => {
  return useMemo(() => {
    return events.map((event, idx) => {
      const isLastEvent = idx === events.length - 1;
      const nextEvent = isLastEvent ? null : events[idx + 1];

      // Check if this is the last agent message in a sequence
      // We want to show the footer when:
      // 1. It's an agent message AND
      // 2. EITHER:
      //    a. The next message is from a user (always show feedback in this case) OR
      //    b. It's the last message AND we're not currently loading a new response
      const isLastAgentInSequence =
        event.author.startsWith(AUTHOR_NEXA_AGENT) &&
        (nextEvent?.author === AUTHOR_USER || (isLastEvent && !isStreaming));

      // Check if there's a non-thought text event after this thought
      // Used to auto-collapse thought blocks when the main answer arrives
      const hasNonThoughtAfter =
        event.thought === true &&
        events.slice(idx + 1).some((e) => e.type === 'text' && !e.thought);

      // Find the preceding user query for retry functionality
      // and collect function_calls that belong to this agent response
      let precedingUserQuery: string | undefined = undefined;
      const functionCalls: FunctionCall[] = [];

      if (isLastAgentInSequence) {
        // Look backward to find the most recent user message and collect function_calls
        for (let i = idx - 1; i >= 0; i--) {
          const prevEvent = events[i];

          if (prevEvent.author === AUTHOR_USER) {
            // Found the user message, stop here
            precedingUserQuery = prevEvent.value;
            break;
          }

          // Collect function_call events (in reverse order, so we'll reverse at the end)
          if (
            prevEvent.type === 'function_call' &&
            prevEvent.author.startsWith(AUTHOR_NEXA_AGENT)
          ) {
            functionCalls.unshift({ id: prevEvent.id || '', value: prevEvent.value });
          }
        }
      }

      // Action bubbles should only appear on the very last agent event overall,
      // not on earlier agent sequences that already have a follow-up user message.
      const isLastAgentEventOverall =
        event.author.startsWith(AUTHOR_NEXA_AGENT) && isLastEvent && !isStreaming;

      return {
        ...event,
        isLastAgentInSequence,
        isLastAgentEventOverall,
        hasNonThoughtAfter,
        precedingUserQuery,
        functionCalls,
      };
    });
  }, [events, isStreaming]);
};

/**
 * Custom hook to automatically scroll to the bottom of a container when dependencies change
 *
 * @param dependencies - Array of dependencies that trigger scrolling when changed
 * @param delay - Optional delay before scrolling (default: 100ms)
 * @returns ref - Reference to attach to the bottom element
 */
export const useScrollToBottom = <T extends HTMLElement>(
  dependencies: any[],
  delay: number = 100,
): RefObject<T> => {
  const bottomRef = useRef<T>(null);

  useEffect(() => {
    setTimeout(() => {
      if (!bottomRef.current) return;
      bottomRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }, delay);
  }, dependencies);

  return bottomRef;
};

export const FALLBACK_ERROR_MESSAGE =
  'I wasn’t able to generate a response this time. Please try again or rephrase your question.';
